import * as log from 'loglevel';

import {html} from 'lit-html';
import {live} from 'lit-html/directives/live';
import {repeat} from 'lit-html/directives/repeat';
import {classMap} from 'lit-html/directives/class-map';
import {styleMap} from 'lit-html/directives/style-map';
import {ifDefined} from 'lit-html/directives/if-defined';
import {unsafeHTML} from 'lit-html/directives/unsafe-html';

import TemplateRegistry from '../template/template-registry';
import {
    optionName, optionClassMap, optionValueClassMap, valueText,
    sizeClassMap, buttonStyleMap
} from '../template/template-util.js';

// one button per gallery view (gallery order) - or the configured values matched case-insensitively to a view, in configured order
// value = canonical view name (exact highlight), text = configured label or the view name
export const viewSwitchValues = (option, views = []) => {
    if(!option.values || !option.values.length){
        return views.map(view => ({ value: view.name, text: view.name }));
    }

    let values = [];

    for (let i = 0; i < option.values.length; i++) {
        const optionValue = option.values[i];
        const view = views.find(view => view.name.toLowerCase() === String(optionValue.value || '').toLowerCase());

        if(view){
            values.push({ value: view.name, text: optionValue.text || view.name });
        } else {
            log.debug('[live-options] view-switch value without a gallery view: ', optionValue.value);
        }
    }

    return values;
};

// button-swatch look, reduced to what a view switch needs: single select, never required, no de-selection, groups or prices
const viewSwitchTemplate = (option, config, values, onSelect) => html`
    <div class="cl-po--swatch-container">
        ${repeat(values, (optionValue) => optionValue.value, (optionValue, index) => html`
            <label class="cl-po--swatch-item ${classMap(optionValueClassMap(option, optionValue, { grow: option.params.grow }))}">
                <input type="radio" class="cl-po--input ${classMap(optionClassMap(option))}" id="${option.id}-${index}" name="${optionName(option)}" .value=${optionValue.value}
                       .checked=${live(option.value == optionValue.value)} form=${ifDefined(config.productFormLink)} @change=${(e) => onSelect(e.target.value)}>
                <div class="cl-po--swatch cl-po--swatch-button ${classMap(sizeClassMap(option, 'small'))}" style=${styleMap(buttonStyleMap(option))}>
                    ${unsafeHTML(valueText(optionValue))}
                </div>
            </label>
            ${option.params.items && (index + 1) % option.params.items === 0 ? html`<div class="cl-po--break" data-index=${index}></div>` : null}
        `)}
    </div>
`;

class ViewSwitchController {

    // capture the owning ProductOptions from its render event (once) - several products can share a form > match the wrapper
    constructor(config){
        this.config = config;
        this.onActive = (e) => this.sync(e.detail.name);
        const onRendered = (e) => {
            if(e.detail?._wrapper === config.wrapper){
                this.productOptions = e.detail;
                config.productForm.removeEventListener('options:rendered', onRendered);
                // the first render event precedes gallery creation
                Promise.resolve().then(() => this.watchGallery());

                // first shown after the preview init (condition): the buttons came from the config > render the real views
                if(this.productOptions._gallery || this.productOptions._preview){
                    this.productOptions.render();
                }
            }
        };
        config.productForm.addEventListener('options:rendered', onRendered);
    }

    watchGallery(){
        const gallery = this.productOptions?._gallery;
        if(gallery === this.gallery){
            return;
        }

        this.gallery?.element?.removeEventListener('preview:active', this.onActive);
        this.gallery = gallery;
        gallery?.ready?.then(() => {
            if(gallery === this.gallery){
                gallery.element.addEventListener('preview:active', this.onActive);
                this.sync(gallery._currentViewName());
            }
        });
    }

    sync(name){
        const value = name || '';
        if(this.option.value !== value){
            this.option.value = value;
            this.render();
        }
    }

    // the value mirrors the gallery: no option:changed (no cart property, no preview re-render) - render re-evaluates conditions & prices
    render(){
        if(this.productOptions){
            this.productOptions.render();
        } else {
            // a programmatic click can precede the first owner event
            this.config.productForm.dispatchEvent(new CustomEvent('options:render', { bubbles: true }));
        }
    }

    // ordered { name, position } views: gallery > single preview > preview config (first render precedes the preview init)
    getViews(){
        const productOptions = this.productOptions;

        if(productOptions?._gallery){
            return productOptions._gallery.getViews();
        }

        let previewConfigs = [];
        if(productOptions?._preview){
            previewConfigs = [productOptions._preview.previewConfig];
        } else if(this.config.previewConfig){
            previewConfigs = [this.config.previewConfig, ...(this.config.previewConfig.views || [])];
        }

        return previewConfigs.map(previewConfig => ({ name: previewConfig.name || 'Main', position: previewConfig.position }));
    }

    select(value){
        // read at click time: the editor replaces the gallery
        const gallery = this.productOptions?._gallery;

        // without an app gallery a click changes nothing: the render resets the buttons (R6)
        if(!gallery?.ready){
            log.warn('[live-options] view-switch needs the app gallery: ', value);
            this.render();
            return;
        }

        this.option.value = value;
        this.render();
        gallery.selectView(value);
    }
}

TemplateRegistry.templates['view-switch'] = (option, config) => {
    // never required: no input attribute and no JS validation error, whatever the config says (R6)
    option.required = false;

    option._controller = option._controller || new ViewSwitchController(config);

    const controller = option._controller;
    controller.option = option;
    controller.watchGallery();

    return viewSwitchTemplate(option, config, viewSwitchValues(option, controller.getViews()), (value) => controller.select(value));
};
